import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { useTranslations } from '@/hooks/useTranslations';
import { LaravelJsonRequestError, laravelJsonFetch } from '@/lib/laravelJsonFetch';
import { Project } from '@/types';
import { router } from '@inertiajs/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

interface Props {
    project: Project;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface ManufacturingNumberResponse {
    success?: boolean;
    message?: string;
    warning?: string | null;
    duplicate?: boolean;
}

export default function ProjectManufacturingNumberModal({ project, open, onOpenChange }: Props) {
    const { t } = useTranslations();
    const [manufacturingNumber, setManufacturingNumber] = useState(project.manufacturing_number || '');
    const [processing, setProcessing] = useState(false);
    const [duplicateWarning, setDuplicateWarning] = useState('');

    const trimmedManufacturingNumber = useMemo(() => manufacturingNumber.trim(), [manufacturingNumber]);
    const isValid = /^[1-9]\d*$/.test(trimmedManufacturingNumber);

    useEffect(() => {
        if (open) {
            setManufacturingNumber(project.manufacturing_number || '');
            setDuplicateWarning('');
        }
    }, [open, project.manufacturing_number]);

    const submit = async (confirmDuplicate = false) => {
        if (!isValid || processing) {
            return;
        }

        setProcessing(true);

        try {
            const response = await laravelJsonFetch<ManufacturingNumberResponse>(route('projects.manufacturing-number.update', project.id), 'PATCH', {
                manufacturing_number: Number(trimmedManufacturingNumber),
                confirm_duplicate: confirmDuplicate,
            });

            toast.success(response.message ?? t('projects.show.manufacturing_number.updated', 'Manufacturing number updated.'));
            setDuplicateWarning('');
            onOpenChange(false);
            router.reload({ only: ['project'] });
        } catch (error) {
            if (error instanceof LaravelJsonRequestError && error.status === 409) {
                const body = error.body as ManufacturingNumberResponse | null;
                setDuplicateWarning(
                    body?.warning ??
                        t(
                            'projects.show.manufacturing_number.duplicate_warning',
                            'Warning: manufacturing number :number is already used by another project this year.',
                        ).replace(':number', trimmedManufacturingNumber),
                );
                return;
            }

            toast.error(t('projects.show.manufacturing_number.update_failed', 'Failed to update manufacturing number.'));
        } finally {
            setProcessing(false);
        }
    };

    return (
        <>
            <ResponsiveModal open={open} onOpenChange={onOpenChange} variant="sheet">
                <ResponsiveModalContent size="md">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('projects.show.manufacturing_number.button', 'Manufacturing number')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t('projects.show.manufacturing_number.description', 'Set a positive manufacturing number for this project.')}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>

                    <ResponsiveModalBody className="flex flex-col gap-2">
                        <Label htmlFor="project-manufacturing-number">
                            {t('projects.show.manufacturing_number.field_label', 'Manufacturing number')}
                        </Label>
                        <Input
                            id="project-manufacturing-number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={manufacturingNumber}
                            onChange={(event) => setManufacturingNumber(event.target.value)}
                        />
                        {!isValid && trimmedManufacturingNumber.length > 0 && (
                            <p className="text-destructive text-sm">
                                {t('projects.show.manufacturing_number.validation_positive', 'Manufacturing number must be a positive integer.')}
                            </p>
                        )}
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button type="button" onClick={() => void submit()} disabled={!isValid || processing}>
                            {processing ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>

            <AlertDialog open={duplicateWarning.length > 0} onOpenChange={(nextOpen) => !nextOpen && setDuplicateWarning('')}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('projects.show.manufacturing_number.duplicate_title', 'Number already used')}</AlertDialogTitle>
                        <AlertDialogDescription>{duplicateWarning}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={processing}>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => void submit(true)} disabled={processing}>
                            {processing ? t('common.saving', 'Saving...') : t('projects.show.manufacturing_number.confirm_save', 'Save anyway')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
